import {
  CALENDAR_HOURS,
  CELL_SHOWN_POSTS,
  CalendarClock,
  CalendarPlacement,
  calendarSlotTime,
  cellKeyOf,
  formatCalendarHour,
  gridOpeningRow,
  isCellFree,
  isCellGone
} from 'utils/socialCampaign/calendar'
import { CalendarPostCover, CalendarPostSummary } from './CalendarPost.component'
import { FC, Fragment, KeyboardEvent, useLayoutEffect, useRef, useState } from 'react'

import { AddCircleOutlineOutlined } from '@mui/icons-material'
import { formatCampaignDay } from 'utils/socialCampaign/campaignStatus'
import { formatRowDate } from 'utils/socialCampaign/schedule'
import styles from './CampaignCalendar.module.sass'
import { useTranslation } from 'react-i18next'

interface Props {
  days: string[]
  /** Bumped by the calendar's bar: scrolls to where the week opens again */
  scrollRequest: number
  placements: CalendarPlacement[]
  cells: Map<string, CalendarPlacement[]>
  clock: CalendarClock
  summaryOf: (placement: CalendarPlacement) => CalendarPostSummary
  onOpenPost?: (postId: string) => void
  /** A free hour that has not gone creates a post there; none is offered without it */
  onAddPostAt?: (slot: { date: string, time: string }) => void
}

/**
 * The week from 861px (prototype v3 `calendarHtml`, v1's `renderWeek`): a time gutter and seven day columns under a
 * sticky header (today underlined), one row per hour from 6:00 to 21:00. A post is its cover filling the hour, its
 * status bar on the left and its time on a dark chip; two posts in one hour share the cell. A gone hour is greyed and
 * takes nothing; a free one shows its "+" on hover or focus and creates a post there. Opens on the week's first post.
 */
export const CampaignCalendarWeek: FC<Props> = ({ days, scrollRequest, placements, cells, clock, summaryOf, onOpenPost, onAddPostAt }) => {
  const { t, i18n } = useTranslation(['social_campaign'])
  const gridRef = useRef<HTMLDivElement>(null)

  // Scrolled to where the week opens when it shows or the bar asks, not each time a refetch brings its posts again
  const monday = days[0]
  useLayoutEffect(() => {
    const grid = gridRef.current
    const row = grid?.querySelector<HTMLElement>(`[data-hour-label="${gridOpeningRow(days, placements, clock)}"]`)
    const head = grid?.querySelector<HTMLElement>(`.${styles.day}`)
    if (grid && row) grid.scrollTop = row.offsetTop - (head?.offsetHeight ?? 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monday, scrollRequest])

  const post = (placement: CalendarPlacement) => {
    const summary = summaryOf(placement)
    const content = (
      <>
        <CalendarPostCover post={placement.post} />
        <span className={styles.slotTime}>{placement.time}</span>
      </>
    )
    return onOpenPost
      ? (
        <button
          key={placement.post.id}
          type="button"
          className={styles.slot}
          data-post={placement.post.id}
          data-status={summary.status}
          aria-label={summary.label}
          title={placement.post.subject}
          onClick={() => onOpenPost(placement.post.id)}
        >
          {content}
        </button>
      )
      : (
        <div key={placement.post.id} className={styles.slot} data-post={placement.post.id} data-status={summary.status} data-static="" role="img" aria-label={summary.label}>
          {content}
        </div>
      )
  }

  // The free hours are one tab stop: the arrow keys move between them, each direction to the next free one
  const isFree = (day: string, hour: number) => isCellFree(day, hour, cells.get(cellKeyOf(day, hour)) ?? [], clock)
  const freeKeys = CALENDAR_HOURS.flatMap((hour) => days.filter((day) => isFree(day, hour)).map((day) => cellKeyOf(day, hour)))
  const [lastFree, setLastFree] = useState<string | null>(null)
  const tabStop = lastFree && freeKeys.includes(lastFree) ? lastFree : freeKeys[0]

  const moveFrom = (day: string, hour: number) => (event: KeyboardEvent) => {
    const step = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key]
    if (!step) return
    event.preventDefault()
    let dayIndex = days.indexOf(day) + step[0]
    let hourIndex = CALENDAR_HOURS.indexOf(hour) + step[1]
    while (dayIndex >= 0 && dayIndex < days.length && hourIndex >= 0 && hourIndex < CALENDAR_HOURS.length) {
      const key = cellKeyOf(days[dayIndex], CALENDAR_HOURS[hourIndex])
      if (freeKeys.includes(key)) {
        gridRef.current?.querySelector<HTMLElement>(`[data-cell="${key}"]`)?.focus()
        return
      }
      dayIndex += step[0]
      hourIndex += step[1]
    }
  }

  return (
    <div ref={gridRef} className={styles.grid}>

      <div className={styles.corner} />

      {days.map((day) => {
        const date = formatRowDate(day, i18n.language)
        return (
          <div key={day} className={styles.day} data-today={day === clock.today || undefined}>
            {`${date.weekday} ${date.dayOfMonth}`}
          </div>
        )
      })}

      {CALENDAR_HOURS.map((hour) => (
        <Fragment key={hour}>
          <div className={styles.hour} data-hour-label={hour}>{formatCalendarHour(hour)}</div>
          {days.map((day) => {
            const key = cellKeyOf(day, hour)
            const here = cells.get(key) ?? []
            const hidden = here.slice(CELL_SHOWN_POSTS)
            return (
              <div key={day} className={styles.cell} data-past={isCellGone(day, hour, here, clock) || undefined}>
                {here.slice(0, CELL_SHOWN_POSTS).map(post)}
                {hidden.length > 0 && onOpenPost &&
                  <button
                    type="button"
                    className={styles.more}
                    aria-label={t('schedule.calendar.more_posts', { count: hidden.length, time: formatCalendarHour(hour) })}
                    onClick={() => onOpenPost(hidden[0].post.id)}
                  >
                    {`+${hidden.length}`}
                  </button>
                }
                {isFree(day, hour) && onAddPostAt &&
                  <button
                    type="button"
                    className={styles.add}
                    // beside a post from before 6:00 or after 21:59, which sits in this row without taking its hour
                    data-beside={here.length > 0 || undefined}
                    data-cell={key}
                    tabIndex={key === tabStop ? 0 : -1}
                    aria-label={t('schedule.calendar.new_post_at', { date: formatCampaignDay(day, i18n.language), time: formatCalendarHour(hour) })}
                    onFocus={() => setLastFree(key)}
                    onKeyDown={moveFrom(day, hour)}
                    onClick={() => onAddPostAt({ date: day, time: calendarSlotTime(hour) })}
                  >
                    <AddCircleOutlineOutlined />
                  </button>
                }
              </div>
            )
          })}
        </Fragment>
      ))}

    </div>
  )
}
